import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  TextField,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import { API_URL } from "../config";
import { KeyboardArrowRight } from "@mui/icons-material";

/* --------------------------------------------------
 *  パスワードバリデーション共通ユーティリティ
 * --------------------------------------------------*/
const MIN_PASSWORD_LEN = 8;
const isValidPassword = (pw) => pw && pw.length >= MIN_PASSWORD_LEN;

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function AdminPage() {
  /* ----------------------------- state ------------------------------ */
  const [users, setUsers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("role");
  const [form, setForm] = useState({
    email: "",
    full_name: "",
    password: "",
    role: "student",
    subject_ids: [],
  });
  const [bulkForm, setBulkForm] = useState({
    emails: "",
    password: "",
    defaultName: "学生",
    subject_ids: [],
  });
  const [message, setMessage] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const SUPER_ADMIN_EMAIL = "ryota.nakamura@ds.musashino-u.ac.jp";

  /* --------------------------- fetchers ----------------------------- */
  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/users/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setUsers(res.data);
      setMessage("");
    } catch {
      setMessage("ユーザ一覧の取得に失敗しました");
    }
  };

  const fetchSubjects = async () => {
    try {
      const res = await axios.get(`${API_URL}/subjects/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setSubjects(res.data);
    } catch (error) {
      console.error("科目一覧の取得に失敗しました", error);
      showNotification("科目一覧の取得に失敗しました", "error");
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchSubjects();
  }, []);

  /* --------------------------- handlers ----------------------------- */
  const handleSort = (prop) => {
    const isAsc = orderBy === prop && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(prop);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/users/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      fetchUsers();
      showNotification("ユーザを削除しました", "success");
    } catch {
      showNotification("削除に失敗しました", "error");
    }
  };

  const handleAdd = async () => {
    /* Front‑side validation */
    if (!isValidPassword(form.password)) {
      showNotification(`パスワードは${MIN_PASSWORD_LEN}文字以上です`, "error");
      return;
    }

    try {
      await axios.post(
        `${API_URL}/users/register`,
        {
          email: form.email,
          password: form.password,
          full_name: form.full_name,
          role: form.role,
          subject_ids: form.subject_ids.map(Number),
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setForm({ email: "", full_name: "", password: "", role: "student", subject_ids: [] });
      fetchUsers();
      showNotification("ユーザを追加しました", "success");
    } catch (e) {
      showNotification(e.response?.data?.detail || "追加に失敗しました", "error");
    }
  };

  const handleBulkAdd = async () => {
    if (!bulkForm.emails.trim()) {
      showNotification("メールアドレスを入力してください", "error");
      return;
    }
    if (!isValidPassword(bulkForm.password)) {
      showNotification(`パスワードは${MIN_PASSWORD_LEN}文字以上です`, "error");
      return;
    }

    const emailList = bulkForm.emails
      .split("\n")
      .map((email) => email.trim())
      .filter((email) => email);

    if (emailList.length === 0) {
      showNotification("有効なメールアドレスがありません", "error");
      return;
    }

    setLoading(true);

    try {
      const results = { success: [], fail: [] };

      for (const email of emailList) {
        try {
          await axios.post(
            `${API_URL}/users/register`,
            {
              email,
              password: bulkForm.password,
              full_name: bulkForm.defaultName,
              role: "student",
              subject_ids: bulkForm.subject_ids.map(Number),
            },
            {
              headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            }
          );
          results.success.push(email);
        } catch (e) {
          results.fail.push({
            email,
            error: e.response?.data?.detail || "追加に失敗しました",
          });
        }
      }

      setBulkForm({ emails: "", password: "", defaultName: "学生", subject_ids: [] });
      fetchUsers();

      const successCount = results.success.length;
      const failCount = results.fail.length;
      showNotification(`${successCount}件追加成功、${failCount}件失敗`, failCount > 0 ? "warning" : "success");
    } catch {
      showNotification("一括登録に失敗しました", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (e, newValue) => setTabValue(newValue);

  const showNotification = (message, severity = "success") => {
    setNotification({ open: true, message, severity });
  };
  const handleCloseNotification = () => setNotification({ ...notification, open: false });

  const sorted = [...users].sort(getComparator(order, orderBy));

  /* ------------------------------ UI -------------------------------- */
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        管理者ユーザ管理
      </Typography>

      {message && (
        <Typography color="error" sx={{ mb: 2 }}>
          {message}
        </Typography>
      )}

      <Tabs value={tabValue} onChange={handleTabChange} centered sx={{ mb: 2 }}>
        <Tab label="個別登録" />
        <Tab label="一括登録" />
        <Tab label="ユーザ一覧" />
      </Tabs>

      {/* ▼▼▼ 個別登録タブ ▼▼▼ */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Paper sx={{ p: 2, backgroundColor: "#000", width: "100%", maxWidth: 800 }}>
            <Typography variant="h6">新規ユーザ追加</Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
              <TextField label="Email" size="small" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />

              <TextField label="Full Name" size="small" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />

              <TextField
                label="Password"
                type="password"
                size="small"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                error={form.password !== "" && !isValidPassword(form.password)}
                helperText={form.password !== "" && !isValidPassword(form.password) ? `パスワードは${MIN_PASSWORD_LEN}文字以上` : " "}
              />

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="role-select-label">Role</InputLabel>
                <Select
                  labelId="role-select-label"
                  value={form.role}
                  label="Role"
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  MenuProps={{ PaperProps: { sx: { bgcolor: "#fff", color: "#000" } } }}
                >
                  <MenuItem value="admin">admin</MenuItem>
                  <MenuItem value="teacher">teacher</MenuItem>
                  <MenuItem value="student">student</MenuItem>
                </Select>
              </FormControl>

              {/* 科目複数選択 */}
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="subject-select-label">科目</InputLabel>
                <Select
                  labelId="subject-select-label"
                  multiple
                  value={form.subject_ids}
                  onChange={(e) => setForm({ ...form, subject_ids: e.target.value.map(Number) })}
                  input={<OutlinedInput label="科目" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((v) => (
                        <Chip key={v} label={subjects.find((s) => s.id === v)?.name || v} size="small" />
                      ))}
                    </Box>
                  )}
                  MenuProps={{ PaperProps: { sx: { bgcolor: "#fff", color: "#000", maxHeight: 300 } } }}
                >
                  {subjects.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      <Checkbox checked={form.subject_ids.indexOf(s.id) > -1} />
                      <ListItemText primary={s.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button variant="contained" size="small" onClick={handleAdd}>
                追加
              </Button>
            </Box>
          </Paper>
        </Box>
      </TabPanel>

      {/* ▼▼▼ 一括登録タブ ▼▼▼ */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Paper sx={{ p: 2, backgroundColor: "#000", width: "100%", maxWidth: 800 }}>
            <Box sx={{ p: 2, mb: 3, borderRadius: 1, backgroundColor: "#1a1a1a", border: "1px solid #9CCB63" }}>
              <Typography variant="subtitle1" sx={{ color: "#9CCB63", fontWeight: "bold" }}>
                🚨 留年リスク早期発見（学生・課題提出状況）
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
                Classroom の提出状況・視聴履歴から、提出率が低い学生を一覧・並べ替えできます。
              </Typography>
              <Button
                component={Link}
                to="/admin/at-risk"
                variant="outlined"
                color="primary"
                endIcon={<KeyboardArrowRight />}
                sx={{ borderColor: "#9CCB63", color: "#9CCB63", "&:hover": { borderColor: "#B8E57F", backgroundColor: "rgba(156,203,99,0.1)" } }}
              >
                留年リスク画面へ
              </Button>
            </Box>
            <Box sx={{ p: 2, mb: 3, borderRadius: 1, backgroundColor: "#1a1a1a", border: "1px solid #9CCB63" }}>
              <Typography variant="subtitle1" sx={{ color: "#9CCB63", fontWeight: "bold" }}>
                💡 より安全な個別パスワード方式の一括登録
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
                各学生に個別のセキュアなパスワードを自動生成し、メールで直接送信できる機能を追加しました。
              </Typography>
              <Button
                component={Link}
                to="/admin/bulk-register"
                variant="outlined"
                color="primary"
                endIcon={<KeyboardArrowRight />}
                sx={{ borderColor: "#9CCB63", color: "#9CCB63", "&:hover": { borderColor: "#B8E57F", backgroundColor: "rgba(156,203,99,0.1)" } }}
              >
                個別パスワード一括登録へ
              </Button>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Typography variant="h6">学生一括登録（共通パスワード）</Typography>
            <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
              メールアドレスを1行に1つずつ入力してください。
            </Typography>

            <TextField
              label="メールアドレス（改行区切り）"
              multiline
              rows={6}
              fullWidth
              margin="normal"
              value={bulkForm.emails}
              onChange={(e) => setBulkForm({ ...bulkForm, emails: e.target.value })}
              placeholder="例：\nstudent1@example.com\nstudent2@example.com\ns1234567@stu.musashino-u.ac.jp"
            />

            <TextField
              label="共通パスワード"
              type="password"
              fullWidth
              margin="normal"
              value={bulkForm.password}
              onChange={(e) => setBulkForm({ ...bulkForm, password: e.target.value })}
              error={bulkForm.password !== "" && !isValidPassword(bulkForm.password)}
              helperText={bulkForm.password !== "" && !isValidPassword(bulkForm.password) ? `パスワードは${MIN_PASSWORD_LEN}文字以上` : " "}
            />

            <TextField
              label="デフォルト氏名"
              fullWidth
              margin="normal"
              value={bulkForm.defaultName}
              onChange={(e) => setBulkForm({ ...bulkForm, defaultName: e.target.value })}
              helperText="※個別に変更がない場合、この名前が使用されます"
            />

            {/* 科目選択 */}
            <FormControl fullWidth margin="normal">
              <InputLabel id="bulk-subject-select-label">科目</InputLabel>
              <Select
                labelId="bulk-subject-select-label"
                multiple
                value={bulkForm.subject_ids}
                onChange={(e) => setBulkForm({ ...bulkForm, subject_ids: e.target.value.map(Number) })}
                input={<OutlinedInput label="科目" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((v) => (
                      <Chip key={v} label={subjects.find((s) => s.id === v)?.name || v} />
                    ))}
                  </Box>
                )}
                MenuProps={{ PaperProps: { sx: { bgcolor: "#fff", color: "#000", maxHeight: 300 } } }}
              >
                {subjects.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    <Checkbox checked={bulkForm.subject_ids.indexOf(s.id) > -1} />
                    <ListItemText primary={s.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button variant="contained" fullWidth sx={{ mt: 2 }} onClick={handleBulkAdd} disabled={loading}>
              {loading ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1, color: "white" }} />
                  処理中...
                </>
              ) : (
                "一括登録"
              )}
            </Button>
          </Paper>
        </Box>
      </TabPanel>

      {/* ▼▼▼ 一覧タブ ▼▼▼ */}
      <TabPanel value={tabValue} index={2}>
        <Table>
          <TableHead>
            <TableRow>
              {['role', 'email', 'id'].map((col) => (
                <TableCell key={col}>
                  <TableSortLabel active={orderBy === col} direction={orderBy === col ? order : 'asc'} onClick={() => handleSort(col)}>
                    {col.toUpperCase()}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell>ACTION</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((u) => (
              <TableRow key={u.id}>
                <TableCell>{u.role}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.id}</TableCell>
                <TableCell>
                  {u.email !== SUPER_ADMIN_EMAIL ? (
                    <Button variant="outlined" size="small" color="error" onClick={() => handleDelete(u.id)}>
                      削除
                    </Button>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TabPanel>

      {/* 通知 */}
      <Snackbar open={notification.open} autoHideDuration={6000} onClose={handleCloseNotification} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseNotification} severity={notification.severity} variant="filled">
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
